// voice-client.ts — die Gesprächs-Verbindung der V8-App: WebRTC (webrtc.ts, livekit-client) im Browser,
// das native Plugin `PawbotVoice` in der App-Hülle. EINE Entscheidungsstelle: `createVoiceClient`.
//
// Aus native-voice.ts gelöst (01.11.2026, E2 docs/v9-stabilisierungsplan.md): native-voice.ts ist die kleine
// Capacitor-Brücke (Erkennung der App, Plugin-Zugriff, Wach-Wort), die auch /neun und der Playground brauchen.
// Nur diese Datei zieht webrtc.ts und damit livekit-client (rund 487 KB) ins Bündel — V9 importiert sie nie.
//
// In der nativen Hülle besitzt ein Swift/Kotlin-Plugin (`PawbotVoice`) die
// LiveKit-Verbindung, das Mikrofon und das Bot-Audio und meldet sie dem OS als
// Anruf (CallKit / Foreground-Service) → das Gespräch läuft im Hintergrund weiter.
// `NativeVoiceClient` ist nur ein dünner Adapter: Token holen → Plugin rufen →
// Plugin-Events auf dieselben Callbacks spiegeln wie webrtc.ts.

import { VoiceClient } from "./webrtc.js";
import { ANRUF_VORBEI } from "./anruf-link.js";
import type { VoiceCallbacks, VoiceState, TokenResponse } from "./webrtc.js";
import {
  isNativePlatform,
  pawbotVoicePlugin,
  type PawbotVoiceEvent,
  type PawbotVoicePlugin,
  type PluginListenerHandle,
} from "./native-voice.js";

/**
 * Öffentlicher Vertrag, den BEIDE Implementierungen erfüllen (DRY): die echte
 * `VoiceClient` (WebRTC) erfüllt ihn strukturell, `NativeVoiceClient` deklariert
 * ihn explizit. `app.ts` hängt nur an diesem Interface, nicht an der Klasse.
 * Exakt die Methoden, die app.ts auf `client` aufruft.
 */
export interface VoiceClientLike {
  /** 2026-08-18: webrtc UND NativeVoiceClient liefern es. Optional bleibt
   *  es fuer aeltere/Teil-Clients. Aufrufer: fehlend = unbekannt; nur
   *  isConnected?.() === false heilt (Self-Heal greift nur, wo der Client
   *  den Zustand wirklich kennt). */
  isConnected?(): boolean;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  /** Ticket 2728: Kennung eines Anrufs von Vespera fuer den NAECHSTEN Token-Abruf. */
  setzeAnrufId(id: string | null): void;
  setMicEnabled(enabled: boolean): Promise<void>;
  setBotAudioMuted(muted: boolean): void;
  sendTextInput(text: string): Promise<void>;
  sendImageInput(url: string, caption?: string): Promise<void>;
  interruptBot(): Promise<void>;
  forceReconnect(reason: string): void;
  noteLocalSpeech(): void;
}

const VALID_STATES: ReadonlySet<string> = new Set<VoiceState>([
  "idle",
  "connecting",
  "reconnecting",
  "listening",
  "thinking",
  "speaking",
  "paused-phone",
  "error",
]);

const DATA_TOPIC = "voice-v8";

/**
 * Dünner Adapter über das native PawbotVoice-Plugin. Spiegelt die öffentliche
 * Schnittstelle von `VoiceClient`. Alle WebRTC-Wächter (Mic-Bytes, Keep-Alive,
 * Stummheits-Detector, Visibility) entfallen bewusst — den Reconnect/Hintergrund
 * besitzt das native Plugin (CallKit/Foreground-Service). Transkript-/Tool-Card-
 * Parsing aus dem Data-Channel folgt in Task 1.5 (geteilter Parser).
 */
class NativeVoiceClient implements VoiceClientLike {
  private readonly callbacks: VoiceCallbacks;
  private plugin: PawbotVoicePlugin | null = null;
  private listeners: PluginListenerHandle[] = [];
  private connecting = false;
  private connected = false;
  /**
   * Ticket 2728, Opus-Prüfung H1 (02.10.2026): Auflegen, WÄHREND `connect()`
   * noch läuft (Abheben über CallKit → Token-Abruf → Martin legt im Anruf-
   * Bildschirm auf). `disconnect()` setzt den Merker; `connect()` prüft ihn nach
   * dem Token-Abruf und nach `plugin.connect` und trennt dann selbst. Vorher lief
   * das Gespräch danach weiter — ohne CallKit, mit offenem Mikro.
   */
  private abgebrochen = false;

  constructor(callbacks: VoiceCallbacks = {}) {
    this.callbacks = callbacks;
  }

  /** Plugin-connected-Flag (lokal gespiegelt nach connect/disconnect). */
  isConnected(): boolean {
    return this.connected;
  }

  private anrufId: string | null = null;
  setzeAnrufId(id: string | null): void {
    this.anrufId = id;
  }

  async connect(): Promise<void> {
    if (this.connected || this.connecting) return;
    const plugin = pawbotVoicePlugin();
    if (!plugin) {
      // Sollte in der nativen Hülle nie passieren; defensiver Fallback statt Crash.
      this.callbacks.onError?.(new Error("PawbotVoice-Plugin nicht verfügbar"));
      this.callbacks.onState?.("error");
      return;
    }
    this.plugin = plugin;
    this.connecting = true;
    this.abgebrochen = false;
    this.callbacks.onState?.("connecting");
    let nativVersucht = false;
    try {
      await this.attachListeners(plugin);
      const { token, url, callId } = await this.fetchToken();
      if (this.abgebrochen) {
        // Aufgelegt während des Token-Abrufs: gar nicht erst verbinden.
        // (disconnect() meldete schon „idle“.)
        await this.detachListeners();
        return;
      }
      nativVersucht = true;
      await plugin.connect({ token, url, ...(callId ? { callId } : {}) });
      if (this.abgebrochen) {
        // Aufgelegt, während LiveKit verband: sofort wieder trennen.
        await this.detachListeners();
        await plugin.disconnect({ auflegen: true }).catch(() => undefined);
        return;
      }
      this.connected = true;
    } catch (err) {
      const fehler = err instanceof Error ? err : new Error(String(err));
      // Die Kennung gilt fuer genau diesen Versuch, auch wenn er vor dem Token scheiterte.
      this.anrufId = null;
      await this.detachListeners();
      // Opus-Prüfung M1: scheitert der native Aufbau mittendrin, kann LiveKit
      // in „connecting“ hängen (und der CallKit-Anruf mit ihm) — aufräumen.
      // Erst NACH dem Abmelden der Lauscher, sonst überschriebe sein „idle“
      // den Fehlerzustand unten.
      // Gescheitert ist gescheitert: auch der CallKit-Anruf endet sofort.
      if (nativVersucht) await plugin.disconnect({ auflegen: true }).catch(() => undefined);
      this.plugin = null;
      if (this.abgebrochen) return; // aufgelegt: kein Fehler zu melden
      this.callbacks.onError?.(fehler);
      // Ticket 2728: ein vorbeigegangener Anruf von Vespera ist kein Fehlerzustand.
      this.callbacks.onState?.(fehler.message === ANRUF_VORBEI ? "idle" : "error");
    } finally {
      this.connecting = false;
    }
  }

  /** Frischer Raum pro Connect — Reconnect/Session-Erhalt liegt nativ beim Plugin. */
  private async fetchToken(): Promise<TokenResponse & { callId: string | null }> {
    const settings = (() => {
      try {
        const raw = localStorage.getItem("voice-v8.settings");
        return raw ? JSON.parse(raw) : undefined;
      } catch {
        return undefined;
      }
    })();
    // Ticket 2728: die Kennung gilt fuer GENAU diesen Abruf (einmalig am Server).
    const callId = this.anrufId;
    this.anrufId = null;
    const res = await fetch("/api/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ settings, ...(callId ? { callId } : {}) }),
    });
    if (!res.ok) {
      if (callId && [404, 409, 410].includes(res.status)) {
        throw new Error(ANRUF_VORBEI);
      }
      throw new Error(`token endpoint failed: ${res.status}`);
    }
    // Die verbrauchte Kennung geht mit an plugin.connect (K1).
    return { ...((await res.json()) as TokenResponse), callId };
  }

  private async attachListeners(plugin: PawbotVoicePlugin): Promise<void> {
    await this.detachListeners();
    const add = async (
      event: PawbotVoiceEvent,
      cb: (data: unknown) => void,
    ): Promise<void> => {
      this.listeners.push(await plugin.addListener(event, cb));
    };
    await add("stateChanged", (data) => {
      const s = (data as { state?: string } | undefined)?.state;
      if (!s || !VALID_STATES.has(s)) return;
      this.zustandMerken(s as VoiceState);
      this.callbacks.onState?.(s as VoiceState);
    });
    await add("reconnecting", (data) => {
      const d = data as { attempt?: number; max?: number } | undefined;
      this.callbacks.onState?.("reconnecting");
      this.callbacks.onReconnectAttempt?.(d?.attempt ?? 1, d?.max ?? 5);
    });
    await add("reconnected", () => {
      this.callbacks.onReconnected?.();
    });
    await add("error", (data) => {
      const msg = (data as { message?: string } | undefined)?.message;
      this.callbacks.onError?.(new Error(msg ?? "native voice error"));
    });
    // Live-Transkript (LiveKit-Segmente, vom Plugin durchgereicht): lokal = User-Text,
    // remote = Bot-Text — auf dieselben Callbacks wie webrtc.ts.
    await add("transcription", (data) => {
      const d = data as
        | { text?: string; final?: boolean; local?: boolean }
        | undefined;
      if (!d || typeof d.text !== "string") return;
      const isFinal = d.final === true;
      if (d.local) {
        this.callbacks.onUserTranscript?.(d.text, isFinal);
      } else {
        this.callbacks.onBotMessage?.(d.text, isFinal);
      }
    });
    // Data-Channel (voice-v8.* — Tool-Karten, Progress, Truth-Warnung etc.).
    // greeting/greeting-tts bewusst NICHT hier: das Bot-Audio + dessen Transkript
    // (onBotMessage via transcription) decken den Begrüßungstext nativ ab → kein Doppel.
    await add("dataReceived", (data) => {
      const raw = (data as { data?: string } | undefined)?.data;
      if (typeof raw === "string") this.handleDataMessage(raw);
    });
    // "audioLevel" → Folge-Schritt (Visualizer).
  }

  /** Mappt eingehende voice-v8.*-Data-Channel-Nachrichten auf die Callbacks —
   *  gleicher Vertrag wie webrtc.ts.handleDataPacket, ohne den WebRTC-eigenen
   *  Greeting-MP3-Pfad (Audio + Transkript erledigen das nativ). #189 Karten. */
  private handleDataMessage(json: string): void {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(json) as Record<string, unknown>;
    } catch {
      return;
    }
    const t = typeof msg.type === "string" ? msg.type : "";
    const cb = this.callbacks;
    if (t === "voice-v8.tool_card") {
      cb.onToolCard?.({
        tool: String(msg.tool ?? ""),
        callId: String(msg.callId ?? ""),
        isError: Boolean(msg.isError),
        result: msg.result,
      });
    } else if (t === "voice-v8.tool_progress") {
      const p = msg.phase;
      const phase =
        p === "started" || p === "running" || p === "done" ? p : "running";
      cb.onToolProgress?.({
        tool: String(msg.tool ?? ""),
        callId: typeof msg.callId === "string" ? msg.callId : undefined,
        phase,
        elapsedMs:
          typeof msg.elapsedMs === "number" ? msg.elapsedMs : undefined,
        lastLine: typeof msg.lastLine === "string" ? msg.lastLine : undefined,
      });
    } else if (t === "voice-v8.truth_warning") {
      cb.onTruthWarning?.({
        reason: typeof msg.reason === "string" ? msg.reason : undefined,
        correction:
          typeof msg.correction === "string" ? msg.correction : undefined,
        latencyMs:
          typeof msg.latencyMs === "number" ? msg.latencyMs : undefined,
      });
    } else if (t === "voice-v8.user_partial" && typeof msg.text === "string") {
      if (msg.text.trim().length > 0) cb.onUserTranscript?.(msg.text, false);
    } else if (t === "voice-v8.user_state") {
      if (
        msg.state === "speaking" ||
        msg.state === "listening" ||
        msg.state === "away"
      ) {
        cb.onUserState?.(msg.state);
      }
    } else if (t === "voice-v8.stt_reject") {
      cb.onSttReject?.({
        text: typeof msg.text === "string" ? msg.text : "",
        reason: typeof msg.reason === "string" ? msg.reason : "unknown",
        noSpeech: typeof msg.noSpeech === "number" ? msg.noSpeech : null,
        avgLogprob: typeof msg.avgLogprob === "number" ? msg.avgLogprob : null,
      });
    } else if (t === "voice-v8.modules") {
      cb.onModulesList?.({
        active: Array.isArray(msg.active) ? (msg.active as string[]) : [],
        inactive: Array.isArray(msg.inactive)
          ? (msg.inactive as Array<{ name: string; reason: string }>)
          : [],
        truthGuard: Boolean(msg.truthGuard),
      });
    } else if (
      t === "voice-v8.state" &&
      (msg.text === "thinking" ||
        msg.text === "listening" ||
        msg.text === "speaking")
    ) {
      cb.onState?.(msg.text);
    } else if (t === "voice-v8.error" && typeof msg.message === "string") {
      const src = typeof msg.source === "string" ? msg.source : "session";
      cb.onError?.(new Error(`[${src}] ${String(msg.message).slice(0, 280)}`));
    }
  }

  private async detachListeners(): Promise<void> {
    for (const h of this.listeners) {
      try {
        h.remove();
      } catch {
        /* egal — Handle evtl. schon entfernt */
      }
    }
    this.listeners = [];
  }

  /** Auflegen (Knopf, Banner, Anruf-Bildschirm) — endet auch in CallKit sofort. */
  async disconnect(): Promise<void> {
    await this.trennen(true);
  }

  private async trennen(auflegen: boolean): Promise<void> {
    // H1: läuft connect() noch, bricht es nach dem nächsten Schritt selbst ab.
    if (this.connecting) this.abgebrochen = true;
    this.connected = false;
    try {
      await this.plugin?.disconnect({ auflegen });
    } finally {
      await this.detachListeners();
      this.plugin = null;
      this.callbacks.onLocalAudio?.(null);
      this.callbacks.onState?.("idle");
    }
  }

  /**
   * Ticket 3182: Solange der native Telefon-Schutz das Mikro wegen eines
   * Fremdtelefonats stumm hält (`paused-phone`), schaltet die Seite es NICHT
   * selbst ein. Mikro-Wünsche (Stumm-Knopf, Fokus-Modus) laufen alle durch
   * `setMicEnabled` — hier wird der letzte Wunsch nur gemerkt und beim
   * Wiederkehren des Zustands einmal nachgereicht.
   */
  private telefonPause = false;
  private offenerMikroWunsch: boolean | null = null;

  private zustandMerken(s: VoiceState): void {
    const war = this.telefonPause;
    this.telefonPause = s === "paused-phone";
    if (war && !this.telefonPause && this.offenerMikroWunsch !== null) {
      const wunsch = this.offenerMikroWunsch;
      this.offenerMikroWunsch = null;
      void this.plugin?.setMicEnabled({ enabled: wunsch });
    }
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    if (this.telefonPause) {
      this.offenerMikroWunsch = enabled;
      return;
    }
    await this.plugin?.setMicEnabled({ enabled });
  }

  setBotAudioMuted(muted: boolean): void {
    void this.plugin?.setBotAudioMuted({ muted });
  }

  async sendTextInput(text: string): Promise<void> {
    await this.publish({ type: "voice-v8.test_input", text });
  }

  async sendImageInput(url: string, caption?: string): Promise<void> {
    await this.publish({
      type: "voice-v8.image_input",
      url,
      caption: caption ?? null,
    });
  }

  async interruptBot(): Promise<void> {
    await this.publish({ type: "voice-v8.interrupt" });
    // Lokaler Sofort-Effekt wie webrtc.ts: Stop-Button reagiert ohne Track-Event.
    this.callbacks.onBotAudio?.(null);
    this.callbacks.onState?.("listening");
  }

  private async publish(message: Record<string, unknown>): Promise<void> {
    if (!this.plugin) return;
    await this.plugin.sendData({
      data: JSON.stringify(message),
      topic: DATA_TOPIC,
      reliable: true,
    });
  }

  /** Native LiveKit reconnectet selbst — ein expliziter Force baut Token+Session neu auf. */
  forceReconnect(reason: string): void {
    console.log(`[native-voice] forceReconnect: ${reason}`);
    // Kein `auflegen`: der Anruf von Vespera bleibt in CallKit stehen (12-s-Nachlauf).
    void this.trennen(false).then(() => this.connect());
  }

  /** No-op: das native Plugin trackt lokale Sprache selbst (kein Web-Wächter nötig). */
  noteLocalSpeech(): void {}
}

/**
 * Eine Entscheidungsstelle (DRY): native Hülle → NativeVoiceClient, sonst die
 * unveränderte WebRTC-`VoiceClient`. app.ts ruft nur das hier.
 */
export function createVoiceClient(
  callbacks: VoiceCallbacks = {},
): VoiceClientLike {
  return isNativePlatform()
    ? new NativeVoiceClient(callbacks)
    : new VoiceClient(callbacks);
}
